import {
  emptyModel,
  newChoiceId,
  newSceneId,
  type CharacterSheet,
  type SceneChoice,
  type StoryModel,
  type StoryScene,
  type VarValue,
} from "@/story/model";

export function emptyCharacter(): CharacterSheet {
  return {
    id: "",
    title: "",
    role: "",
    blurb: "",
    avatarEmoji: "🧑‍💻",
    color: "#3ddc97",
    stats: [],
    traits: [],
    tags: [],
    public: false,
  };
}

export function starterModel(schema: "linear" | "roguelike"): StoryModel {
  const model = emptyModel(schema);
  const taken = new Set<string>();

  if (schema === "roguelike") {
    model.variables = {
      health: 100,
      floor_number: 1,
      tier: 1,
      tokens: 0,
      debugger_charges: 2,
    };
    model.messages = {
      game_over: "SPRINT TERMINATED",
      boss: "BUGS FORM A WALL",
    };

    const startId = newSceneId("The Desk", taken);
    const lobbyId = newSceneId("The Lobby", taken);
    const buildRoomId = newSceneId("The Build Room", taken);
    const bossId = newSceneId("The Gradle Daemon", taken);
    const shippedId = newSceneId("Shipped", taken);
    const failId = newSceneId("Bankrupt", taken);
    model.start = startId;

    model.scenes = [
      narrativeScene(startId, "The Desk", "run_start", "#141b33", [
        "You crack open the laptop. Below the noise, the codebase slumbers.",
        "Somewhere down there the build is waiting to hear from you.",
      ]),
      floorScene(lobbyId, "The Lobby", "#173a2e", 1, [
        "Rows of monitors flicker with TODO comments nobody ever finished.",
      ]),
      floorScene(buildRoomId, "The Build Room", "#3a2415", 2, [
        "Compilers scream. One of them calls you by name.",
      ]),
      floorScene(bossId, "The Gradle Daemon", "#4a000a", 3, [
        "A bloated daemon blocks the deploy path, breathing dependency soup.",
      ]),
      endingScene(shippedId, "Shipped", "shipped", "#3ddc97", [
        "The green checkmark glows. Somewhere, a release train departs.",
        "YOU SHIPPED IT.",
      ]),
      endingScene(failId, "Bankrupt", "fail", "#4a000a", [
        "The keyboard fizzes out. The sprint is over before the standup.",
      ]),
    ];

    const lobby = model.scenes[1]!;
    const buildRoom = model.scenes[2]!;
    const boss = model.scenes[3]!;
    lobby.choices = [
      ch(
        "Take the elevator down",
        ["Floor number {floor_number}. The descent begins."],
        startId
      ),
      ch(
        "Push straight to production",
        ["The monitors hiss. They know what you did."],
        shippedId,
        { tags: ["MESSAGE::game_over"] }
      ),
    ];
    buildRoom.choices = [
      ch(
        "Investigate the failing test",
        ["The test bites back at your health."],
        startId,
        { add: { health: -15 } }
      ),
      ch("Ship anyway", ["The build hisses its disapproval."], startId),
    ];
    boss.choices = [
      ch(
        "Run the ancient debugger",
        ["It works. Barely. The daemon collapses in a puff of logs."],
        shippedId,
        { add: { health: -25 } }
      ),
    ];
  } else {
    const startId = newSceneId("Beginning", taken);
    const middleId = newSceneId("Middle", taken);
    const endId = newSceneId("The End", taken);
    model.start = startId;

    const beginning = narrativeScene(
      startId,
      "Beginning",
      "beginning",
      "#141b33",
      ["A freshly cloned repository. The first commit writes itself."]
    );
    beginning.choices = [
      ch(
        "Make the first commit",
        ["git add . && git commit -m 'it begins'"],
        middleId
      ),
    ];

    const middle = narrativeScene(middleId, "Middle", "middle", "#173a2e", [
      "Merge conflicts drift past like icebergs.",
    ]);
    middle.choices = [
      ch(
        "Rebase and carry on",
        ["History rewrites itself. Nobody gets hurt."],
        endId
      ),
      ch("Force push and flee", ["Somewhere, a repo weeps."], endId),
    ];

    const theEnd = endingScene(endId, "The End", "the_end", "#3ddc97", [
      "CI is green. You close the laptop like a cowboy.",
    ]);

    model.scenes = [beginning, middle, theEnd];
  }

  return model;
}

function ch(
  label: string,
  lines: string[],
  target: string | null,
  opts: {
    condition?: string;
    set?: Record<string, VarValue>;
    add?: Record<string, VarValue>;
    tags?: string[];
  } = {}
): SceneChoice {
  return {
    id: newChoiceId(),
    label,
    condition: opts.condition ?? "",
    set: opts.set ?? {},
    add: opts.add ?? {},
    lines,
    tags: opts.tags ?? [],
    target,
  };
}

function narrativeScene(
  id: string,
  title: string,
  sceneKey: string,
  color: string,
  lines: string[]
): StoryScene {
  return {
    id,
    title,
    kind: "narrative",
    sceneKey,
    color,
    tier: 1,
    tags: [],
    lines,
    choices: [],
  };
}

function floorScene(
  id: string,
  title: string,
  color: string,
  tier: 1 | 2 | 3,
  lines: string[]
): StoryScene {
  return {
    id,
    title,
    kind: "floor",
    sceneKey: id,
    color,
    tier,
    tags: [],
    lines,
    choices: [],
  };
}

function endingScene(
  id: string,
  title: string,
  sceneKey: string,
  color: string,
  lines: string[]
): StoryScene {
  return {
    id,
    title,
    kind: "ending",
    sceneKey,
    color,
    tier: 1,
    tags: [`ENDING::${sceneKey}`],
    lines,
    choices: [],
  };
}

export function newScenePlaceholder(
  kind: StoryScene["kind"],
  idOffset: string
): StoryScene {
  return {
    id: `scene_${idOffset}`,
    title: "New scene",
    kind,
    sceneKey: `scene_${idOffset}`,
    color: "#1a1a2e",
    tier: 1,
    tags: [],
    lines: [],
    choices: [],
  };
}
